import { Link } from 'react-router-dom';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import FeedbackForm from './components/FeedbackForm';
import { categories } from '@/mocks/categories';
import { films } from '@/mocks/films';
import { usePageMeta } from '@/hooks/usePageMeta';

const principles = [
  {
    icon: 'ri-file-list-3-line',
    title: '条目信息完整',
    text: '每一条收录都包含导演、主演、年份、地区、片长与类型，避免只有标题没有资料的「空心条目」。',
  },
  {
    icon: 'ri-layout-grid-line',
    title: '分类结构清晰',
    text: '按题材与出品方向划分主要分类，再以子分类细化，配合地区与年份筛选，减少翻找成本。',
  },
  {
    icon: 'ri-shield-check-line',
    title: '只做资料展示',
    text: '本站不提供播放、下载与交易功能，所有内容以图文资料形式呈现，用于了解与查找。',
  },
];

export default function AboutPage() {
  usePageMeta({
    title: '关于我们与联系方式 - 成人影视',
    description:
      '了解成人影视网站的定位、内容范围与收录原则，查看联系方式、反馈渠道及版权与免责声明。',
    keywords: '成人影视,关于我们,联系方式,内容声明,影视资料库',
  });

  return (
    <div className="min-h-screen bg-background-50">
      <SiteHeader />

      <main>
        <section className="border-b border-background-300/70 bg-background-100">
          <div className="page-shell py-10 md:py-14">
            <nav className="mb-5 flex items-center gap-2 text-[11px] text-foreground-500">
              <Link to="/" className="transition-colors hover:text-primary-400">首页</Link>
              <i className="ri-arrow-right-s-line" />
              <span className="text-foreground-800">关于我们</span>
            </nav>
            <div className="grid gap-8 md:grid-cols-12">
              <div className="md:col-span-7">
                <p className="eyebrow mb-2">About the archive</p>
                <h1 className="text-2xl font-semibold leading-tight text-foreground-950 md:text-[34px]">
                  一个认真整理影视资料的站点
                </h1>
                <p className="mt-4 text-[13px] leading-8 text-foreground-600">
                  成人影视是一个面向成年观众的影视内容资料与资讯平台。我们相信，比起花哨的入口，清晰的信息结构更能帮人找到想看的内容：条目写清楚，分类分明白，检索做得准确。
                </p>
                <p className="mt-3 text-[13px] leading-8 text-foreground-600">
                  站点当前处于演示阶段，收录条目、人物名称与资讯内容均为示例数据，用于展示页面结构与交互逻辑，不代表真实作品或真实人物。
                </p>
              </div>
              <div className="md:col-span-5">
                <div className="grid grid-cols-3 gap-3 rounded-lg border border-background-300/70 bg-background-50 p-5">
                  {[
                    { value: films.length, label: '收录条目' },
                    { value: categories.length, label: '主要分类' },
                    { value: '4', label: '筛选维度' },
                  ].map((stat) => (
                    <div key={stat.label} className="text-center">
                      <p className="font-heading text-2xl font-semibold text-primary-500">{stat.value}</p>
                      <p className="mt-1 text-[11px] text-foreground-500">{stat.label}</p>
                    </div>
                  ))}
                </div>
                <div className="mt-3 rounded-lg border border-accent-400/40 bg-accent-500/[0.08] p-4">
                  <p className="flex items-center gap-2 text-[12px] text-accent-300">
                    <i className="ri-forbid-line" />
                    仅面向 18 岁以上成年用户
                  </p>
                  <p className="mt-2 text-[11px] leading-6 text-foreground-500">
                    未成年人请勿访问本站。访问前请确认你已符合所在地法律法规对成年内容浏览的要求。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="page-shell pt-12 md:pt-16">
          <div className="mb-6">
            <p className="eyebrow mb-2">Content scope</p>
            <h2 className="text-xl font-semibold text-foreground-950 md:text-2xl">内容范围与收录原则</h2>
          </div>
          <div className="grid gap-4 md:grid-cols-3">
            {principles.map((item) => (
              <div key={item.title} className="rounded-lg border border-background-300/70 bg-background-100 p-5">
                <span className="flex h-10 w-10 items-center justify-center rounded-md bg-background-200 text-primary-400">
                  <i className={`${item.icon} text-lg`} />
                </span>
                <h3 className="mt-3.5 text-[14px] font-medium text-foreground-950">{item.title}</h3>
                <p className="mt-2 text-[12px] leading-6 text-foreground-600">{item.text}</p>
              </div>
            ))}
          </div>

          <div className="mt-4 grid gap-4 md:grid-cols-2">
            <div className="rounded-lg border border-background-300/70 bg-background-100 p-5">
              <h3 className="mb-3 flex items-center gap-2 text-[14px] font-medium text-foreground-950">
                <i className="ri-check-double-line text-primary-400" />
                我们收录
              </h3>
              <ul className="space-y-2 text-[12px] leading-6 text-foreground-600">
                <li>· 作品条目：片名、导演、主演、年份、地区、片长与简介</li>
                <li>· 资讯动态：新片消息、修复重映、影展片单与发行信息</li>
                <li>· 幕后记录：剧组岗位、制作流程与从业者访谈</li>
              </ul>
            </div>
            <div className="rounded-lg border border-background-300/70 bg-background-100 p-5">
              <h3 className="mb-3 flex items-center gap-2 text-[14px] font-medium text-foreground-950">
                <i className="ri-close-circle-line text-accent-400" />
                我们不提供
              </h3>
              <ul className="space-y-2 text-[12px] leading-6 text-foreground-600">
                <li>· 在线播放、下载与任何形式的资源分发</li>
                <li>· 账号注册、付费订阅与交易功能</li>
                <li>· 任何未经授权的内容与侵权素材</li>
              </ul>
            </div>
          </div>
        </section>

        <section className="page-shell pt-12 md:pt-16">
          <div className="grid gap-8 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <p className="eyebrow mb-2">Contact</p>
              <h2 className="text-xl font-semibold text-foreground-950 md:text-2xl">联系我们</h2>
              <p className="mt-3 text-[13px] leading-7 text-foreground-600">
                如果发现条目信息有误、需要补充资料，或涉及版权与删除请求，可以通过右侧表单反馈。我们会在收到后尽快核对处理。
              </p>
              <ul className="mt-6 space-y-3">
                {[
                  { icon: 'ri-mail-line', label: '内容与纠错', value: 'archive@example.com' },
                  { icon: 'ri-shield-keyhole-line', label: '版权与删除', value: 'rights@example.com' },
                  { icon: 'ri-time-line', label: '处理时效', value: '工作日 3 个工作日内回复' },
                ].map((row) => (
                  <li key={row.label} className="flex items-start gap-3 rounded-lg border border-background-300/70 bg-background-100 p-4">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-background-200 text-primary-400">
                      <i className={`${row.icon} text-[15px]`} />
                    </span>
                    <div className="min-w-0">
                      <p className="text-[11px] text-foreground-500">{row.label}</p>
                      <p className="mt-0.5 break-words text-[12px] text-foreground-800">{row.value}</p>
                    </div>
                  </li>
                ))}
              </ul>
              <p className="mt-4 text-[11px] leading-6 text-foreground-500">
                以上联系方式为演示信息，实际使用前请替换为真实可用的邮箱地址。
              </p>
            </div>
            <div className="lg:col-span-7">
              <FeedbackForm />
            </div>
          </div>
        </section>

        <section className="page-shell pt-12 md:pt-16">
          <div className="rounded-lg border border-background-300/70 bg-background-100 p-6 md:p-8">
            <h2 className="mb-4 flex items-center gap-2 text-[15px] font-medium text-foreground-950">
              <i className="ri-scales-3-line text-primary-400" />
              版权与免责声明
            </h2>
            <div className="space-y-3 text-[12px] leading-7 text-foreground-600">
              <p>
                本站为前端演示项目，所有影视条目、人物名称、评分与资讯内容均为虚构示例，仅用于展示页面结构与交互效果，与任何真实作品或真实人物无关。
              </p>
              <p>
                本站不存储、不传播任何视频文件，不提供在线播放或下载入口。站内出现的图片均为示意性生成素材。
              </p>
              <p>
                本站内容仅面向 18 岁以上成年用户。访问者应在访问前确认自身年龄及所在地法律法规的允许范围，并自行承担相应责任。
              </p>
              <p>
                如权利人认为站内文字或图片存在侵权，可通过上方反馈渠道提交说明，我们会核对后及时处理。
              </p>
            </div>
            <div className="mt-6 flex flex-wrap items-center gap-3 border-t border-background-300/70 pt-5">
              <Link
                to="/"
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-[12px] font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-home-4-line" />
                返回首页
              </Link>
              <Link
                to="/search"
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-4 py-2.5 text-[12px] text-foreground-700 transition-colors hover:border-primary-500/60 hover:text-primary-400"
              >
                <i className="ri-search-line" />
                检索内容
              </Link>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}